/**
 * 框架通道配置页的按通道文案与录入约定（PAYERMAX / AIRWALLEX），配置页、抽屉、验签材料弹窗与错误提示共用。
 *
 * 只管「怎么称呼、怎么录」，凭据字段闭集、必填与是否秘密一律取能力声明（A8 §10.0）。依据 dev-docs
 * `02-API契约/65-三方支付通道框架.md`：
 *   - §2.1 PAYERMAX：`merchant_no` 与 `merchant_account_id` 逐字相等；回调验签材料 = 平台公钥 = 凭据
 *     `platform_public_key`（A8 §10.2「验签材料与凭据字段一致」）；两把密钥按 PEM 多行录入。
 *   - §2.2 AIRWALLEX：凭据 `client_id` / `api_key` 与 `merchant_account_id` 无一致性约束（账户归属由连接测试确认）；
 *     回调验签材料 = webhook 订阅的通知密钥（共享密钥），不属于凭据字段，按秘密遮蔽录入。
 * 两家都没有设计稿外的专属界面：组件与布局沿用 PayerMax 设计稿（A0–A6），只换文案。
 */

import type { PaymentProviderChannel } from '@novel-admin/shared';

export type ProviderChannelCopy = {
  /** 供应商展示名（UI 文案） */
  displayName: string;
  /** 新增配置时商户号输入框的提示 */
  merchantAccountHint: string;
  /** 与 `merchant_account_id` 逐字相等、由商户号同步的凭据字段；无一致性约束的通道为 null */
  merchantCredentialField: string | null;
  /** 与回调验签材料为同一把的凭据字段；验签材料不属于凭据字段的通道为 null */
  verificationCredentialField: string | null;
  /** 按 PEM 多行录入的凭据字段 */
  multilineCredentialFields: readonly string[];
  /** 回调验签材料的称呼 */
  materialName: string;
  /** 验签材料是否为秘密（写入弹窗遮蔽输入） */
  materialSecret: boolean;
  /** 验签材料是否按 PEM 多行粘贴 */
  materialMultiline: boolean;
  /** 验签材料输入框占位提示 */
  materialPlaceholder: string;
  /** 验签材料形态校验失败（`VALIDATION_FAILED · material`）的标题与说明 */
  materialInvalidTitle: string;
  materialInvalidBody: string;
  /**
   * 同步响应是否以商户账户组验签材料验签（A8 §10.2 规则 4）：是则组内没有当前有效材料时新发起与长期读取都无法完成；
   * AIRWALLEX 同步响应不验签（65 §2.2），材料只影响回调与 test / activate 前置。
   */
  syncResponseUsesMaterial: boolean;
  /** 运营在供应商侧填回调地址 / 取验签材料的位置 */
  consoleName: string;
  /** 连接测试凭据被拒时的说明 */
  credentialRejectedBody: string;
};

export const PROVIDER_CHANNEL_COPY: Record<PaymentProviderChannel, ProviderChannelCopy> = {
  PAYERMAX: {
    displayName: 'PayerMax',
    merchantAccountHint: 'PayerMax 商户账户标识 · 1–128 字符',
    merchantCredentialField: 'merchant_no',
    verificationCredentialField: 'platform_public_key',
    multilineCredentialFields: ['merchant_private_key', 'platform_public_key'],
    materialName: '平台公钥',
    materialSecret: false,
    materialMultiline: true,
    materialPlaceholder: '粘贴 PayerMax 平台公钥，带或不带 PEM 头尾均可',
    materialInvalidTitle: '不是有效的 PayerMax 平台公钥',
    materialInvalidBody: '可能被截断，或误贴了私钥。',
    syncResponseUsesMaterial: true,
    consoleName: 'PayerMax 后台',
    credentialRejectedBody:
      '商户号、应用 ID、私钥、平台公钥或 key 版本有误，或填的是另一环境的凭据。',
  },
  AIRWALLEX: {
    displayName: 'Airwallex',
    merchantAccountHint: 'Airwallex 账户标识（account_id）· 1–128 字符',
    merchantCredentialField: null,
    verificationCredentialField: null,
    multilineCredentialFields: [],
    materialName: '通知密钥',
    materialSecret: true,
    materialMultiline: false,
    materialPlaceholder: '粘贴 Airwallex 后台 webhook 订阅的通知密钥',
    materialInvalidTitle: '不是有效的 Airwallex 通知密钥',
    materialInvalidBody: '通知密钥是一段不含空格与换行的字符串，可能多粘了内容或贴成了别的密钥。',
    syncResponseUsesMaterial: false,
    consoleName: 'Airwallex 后台',
    credentialRejectedBody: 'Client ID 或 API key 有误，或填的是另一环境（沙盒 / 生产）的凭据。',
  },
};

/** 中性文案：拿不到通道时（错误 details 不带 channel 且调用方未传）不冒用某一家的名字。 */
const NEUTRAL_DISPLAY_NAME = '供应商';

export function providerChannelCopy(channel: PaymentProviderChannel): ProviderChannelCopy {
  return PROVIDER_CHANNEL_COPY[channel];
}

export function providerDisplayName(channel: PaymentProviderChannel | null | undefined): string {
  return channel ? PROVIDER_CHANNEL_COPY[channel].displayName : NEUTRAL_DISPLAY_NAME;
}
